import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePageContext } from '../../context/PageContext/PageContext';
import Step1Paste from './Step1Paste';
import Step2Errors from './Step2Errors';
import Step3PreFlight from './Step3PreFlight';
import Step4Progress from './Step4Progress';
import Step5Summary from './Step5Summary';
import { previewBulk, startBulk, fetchBulkStatus } from './bulkApi';
import './BulkUpload.css';

const STAGE_TITLES = {
    paste: 'Bulk Upload',
    errors: 'Bulk Upload — Fix Errors',
    preflight: 'Bulk Upload — Pre-flight',
    progress: 'Bulk Upload — Enriching',
    summary: 'Bulk Upload — Complete',
};

export default function BulkUploadPage() {
    const navigate = useNavigate();
    const { setPageTitle } = usePageContext();

    const [stage, setStage] = useState('paste');
    const [csvText, setCsvText] = useState('');
    const [previewResult, setPreviewResult] = useState(null);
    const [validRows, setValidRows] = useState([]);
    const [invalidRows, setInvalidRows] = useState([]);
    const [jobId, setJobId] = useState(null);
    const [jobStatus, setJobStatus] = useState(null);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        setPageTitle(STAGE_TITLES[stage] || 'Bulk Upload');
    }, [setPageTitle, stage]);

    const handleValidate = async () => {
        setError('');
        setLoading(true);
        try {
            const result = await previewBulk(csvText);
            setPreviewResult(result);
            setValidRows(result.valid || []);
            setInvalidRows(result.invalid || []);
            if ((result.invalid || []).length > 0) {
                setStage('errors');
            } else {
                setStage('preflight');
            }
        } catch (err) {
            const data = err.response?.data;
            if (err.response?.status === 429) {
                setError('Too many validation attempts. Please wait a moment and try again.');
            } else if (data?.error === 'too many rows') {
                setError(`Too many rows. Cap is ${data.cap ?? 100} (received ${data.received}).`);
            } else if (data?.error === 'oversized') {
                setError(data.message || 'Paste is too long. Please reduce to under 50KB.');
            } else if (data?.error === 'could not parse CSV') {
                setError(`Couldn't parse your paste. ${data.detail || 'Try following the example format.'}`);
            } else {
                setError('Validation failed. Please check your paste and try again.');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleStart = async () => {
        setError('');
        setLoading(true);
        try {
            const { jobId: newJobId } = await startBulk(validRows);
            setJobId(newJobId);
            setStage('progress');
        } catch (err) {
            const data = err.response?.data;
            if (err.response?.status === 409) {
                setError('You already have a bulk upload in progress. Wait for it to finish.');
            } else if (err.response?.status === 429) {
                setError('Too many bulk uploads. Please wait before starting another.');
            } else {
                setError(data?.error || 'Failed to start bulk upload.');
            }
        } finally {
            setLoading(false);
        }
    };

    const pollStatus = useCallback(async () => {
        if (!jobId) return;
        try {
            const status = await fetchBulkStatus(jobId);
            setJobStatus(status);
            if (status.status === 'complete' || status.status === 'partial' || status.status === 'failed') {
                setStage('summary');
            }
        } catch {
            // keep polling on transient errors
        }
    }, [jobId]);

    useEffect(() => {
        if (stage !== 'progress' || !jobId) return undefined;

        pollStatus();
        const interval = setInterval(pollStatus, 2000);
        return () => clearInterval(interval);
    }, [stage, jobId, pollStatus]);

    const handleStartOver = () => {
        setStage('paste');
        setCsvText('');
        setPreviewResult(null);
        setValidRows([]);
        setInvalidRows([]);
        setJobId(null);
        setJobStatus(null);
        setError('');
    };

    const handleOpenMyProperties = () => {
        navigate('/dashboard/my-properties', { state: { scrollToBulk: true } });
    };

    const skippedCount = previewResult
        ? (previewResult.invalid?.length ?? invalidRows.length)
        : invalidRows.length;

    return (
        <div className="parkpro-bulk-page">
            <div className="bulk-body">
                <h1 className="bulk-heading">Upload properties via CSV</h1>
                <p className="bulk-sub">Paste your CSV below. We&apos;ll validate, then enrich each property concurrently with AI.</p>

                {stage === 'paste' && (
                    <Step1Paste
                        csvText={csvText}
                        setCsvText={setCsvText}
                        error={error}
                        loading={loading}
                        onCancel={() => navigate('/dashboard/my-properties')}
                        onValidate={handleValidate}
                    />
                )}

                {stage === 'errors' && (
                    <Step2Errors
                        valid={validRows}
                        invalid={invalidRows}
                        droppedLines={previewResult?.droppedLines}
                        onUpdate={(v, inv) => {
                            setValidRows(v);
                            setInvalidRows(inv);
                        }}
                        onBack={() => setStage('paste')}
                        onContinue={() => setStage('preflight')}
                    />
                )}

                {stage === 'preflight' && (
                    <Step3PreFlight
                        validCount={validRows.length}
                        skippedCount={skippedCount}
                        loading={loading}
                        error={error}
                        onStart={handleStart}
                        onBack={() => (invalidRows.length > 0 ? setStage('errors') : setStage('paste'))}
                    />
                )}

                {stage === 'progress' && (
                    <Step4Progress jobStatus={jobStatus} />
                )}

                {stage === 'summary' && (
                    <Step5Summary
                        jobStatus={jobStatus}
                        onStartOver={handleStartOver}
                        onOpenMyProperties={handleOpenMyProperties}
                    />
                )}
            </div>
        </div>
    );
}
